import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { FileDownloadOutlined as ExportIcon } from "@mui/icons-material";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { baseAxios } from "../../../utils/config";

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Stylist Insights analytics table
 */
export function StylistInsightsTab({
  salon,
  startDate,
  endDate,
  formatDateToISO,
  user,
  needsLocation,
  refreshKey,
  hasRun,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("total_formulas");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadData = useCallback(async () => {
    if (needsLocation || !salon) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        locationId: salon?.location_id || user.location_id,
      };
      if (startDate) params.startDate = formatDateToISO(startDate);
      if (endDate) params.endDate = formatDateToISO(endDate);

      const response = await baseAxios.get(
        "/formula-tracker/analytics/stylists",
        { params }
      );
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch {
      setRows([]);
      toast.error("Failed to load stylist insights", { duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [
    endDate,
    formatDateToISO,
    needsLocation,
    salon,
    startDate,
    user.location_id,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Stylist Insights");
      worksheet.columns = [
        { header: "Stylist", key: "stylist_name", width: 22 },
        { header: "Total Formulas", key: "total_formulas", width: 14 },
        { header: "Unique Recipes", key: "unique_recipes", width: 14 },
        { header: "Diversity Score", key: "formula_diversity", width: 14 },
        {
          header: "Avg Products/Formula",
          key: "avg_products_per_formula",
          width: 18,
        },
        { header: "Consistency Rate", key: "consistency_rate", width: 16 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => worksheet.addRow(row));
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `StylistInsights_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "stylist_name",
        label: "Stylist",
        field: "stylist_name",
        minWidth: 160,
        render: (v) => v || "—",
      },
      {
        id: "total_formulas",
        label: "Total Formulas",
        field: "total_formulas",
        minWidth: 130,
        align: "center",
        render: (v) => Number(v) || 0,
      },
      {
        id: "unique_recipes",
        label: "Unique Recipes",
        field: "unique_recipes",
        minWidth: 130,
        align: "center",
        render: (v) => Number(v) || 0,
      },
      {
        id: "formula_diversity",
        label: "Diversity Score",
        field: "formula_diversity",
        minWidth: 130,
        align: "center",
        render: (v) =>
          v == null || Number.isNaN(Number(v)) ? "0.00" : Number(v).toFixed(2),
      },
      {
        id: "avg_products_per_formula",
        label: "Avg Products/Formula",
        field: "avg_products_per_formula",
        minWidth: 160,
        align: "center",
        render: (v) =>
          v == null || Number.isNaN(Number(v)) ? "0.0" : Number(v).toFixed(1),
      },
      {
        id: "consistency_rate",
        label: "Consistency Rate",
        field: "consistency_rate",
        minWidth: 140,
        align: "center",
        render: (v) =>
          v == null || Number.isNaN(Number(v))
            ? "0.0%"
            : `${Number(v).toFixed(1)}%`,
      },
    ],
    []
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Typography fontWeight={600}>Stylist Formula Insights</Typography>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          disabled={loading || !sortedRows.length}
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 36 }}
        >
          Export
        </Button>
      </Box>
      <PaginatedTable
        columns={columns}
        rows={sortedRows}
        totalRowCount={sortedRows.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="total_formulas"
        initialOrder="desc"
        searchQuery={`stylist|${refreshKey}|${salon?.location_id}|${formatDateToISO(startDate)}|${formatDateToISO(endDate)}`}
        clientSidePagination
        skeletonHeight="40px"
        getRowKey={(row, i) => row.stylist_id ?? `stylist-${i}`}
        emptyMessage={
          hasRun
            ? "No stylist insights for this range"
            : "Click Load Analytics to view data"
        }
      />
    </Box>
  );
}
